<ng-template let-extraClass="extraClass" #topNav>
  @if (!windowService?.isDesktopRes) {
    <tidy-top-nav
      [class]="extraClass"
      [title]="state === 'report' ? 'Optimization Report' : (listing ? 'Listing Details' : 'Add Listing')"
      [canGoBack]="true"
      [customBackAction]="topNavBack"
      [linkIcon]="'share'"
      [showLinkIcon]="state === 'report' && !!selectedRunReport"
      (linkAction)="shareReport()"
      [showDropdownMenu]="mobileDropdownMenu.length > 0"
      [dropdownMenu]="mobileDropdownMenu" />
  }
</ng-template>

<ng-container *ngTemplateOutlet="topNav;context:{ extraClass: 'mobile' }" />

<ng-template #content>
  <tidy-wrapper>
    @if (state === 'loading') {
      <div class="loading-container">
        <tidy-loader />
      </div>
    }

    @if (state === 'no-property') {
      <tidy-row class="extra-bottom-padding">
        <tidy-text
          [body]="'Add a property before connecting a listing. Once you have a property, you can link its Airbnb, VRBO, or Booking.com listing here.'" />
      </tidy-row>
      <tidy-row [alignLastItemRight]="true">
        <tidy-button
          [text]="'Add Property'"
          [action]="goToAddProperty"
          class="primary" />
      </tidy-row>
    }

    @if (state === 'add') {
      <tidy-row class="extra-bottom-padding">
        <tidy-text
          [body]="'Connect your listing by selecting the platform and entering your listing ID.'" />
      </tidy-row>

      <tidy-row>
        <form [formGroup]="form">
          <tidy-select
            [label]="'Property'"
            [form]="form.controls.address_id"
            formControlName="address_id"
            [items]="addressItems"
            [icon]="'assets/svg/home-outline.svg'"
            (optionChange)="onAddressChange($event)" />
        </form>
      </tidy-row>

      @if (listingTargetOptions.length) {
        <tidy-row>
          <form [formGroup]="form">
            <tidy-select
              [label]="'What are you listing?'"
              [form]="form.controls.listing_target"
              formControlName="listing_target"
              [items]="listingTargetOptions"
              [icon]="'assets/svg/list.svg'"
              (optionChange)="selectTarget($event)" />
          </form>
        </tidy-row>
      }

      @for (group of platformGroups; track group.category; let isFirst = $first) {
        <tidy-row [class]="isFirst ? '' : 'extra-top-padding'">
          <tidy-text [fieldLabel]="group.label" class="field-label" />
          <div class="platform-options">
            @for (item of group.items; track item.value) {
              <tidy-card
                class="platform-option"
                [ngClass]="{'platform-option-selected': form.value.platform === item.value}"
                (click)="selectPlatform(item.value)">
                <tidy-image [src]="item.logo" class="platform-option-logo" />
                <tidy-text [body]="item.viewValue" [translate]="false" />
              </tidy-card>
            }
          </div>
        </tidy-row>
      }
      @if (submitted && !form.controls.platform.valid) {
        <tidy-row>
          <tidy-error-message [text]="'Please select a platform'" />
        </tidy-row>
      }

      <tidy-row>
        <form [formGroup]="form">
          <tidy-input
            [label]="listingIdLabel"
            formControlName="listingId"
            [form]="form.controls.listingId"
            [icon]="'assets/svg/home-outline.svg'"
            [submitted]="submitted"
            [errorMessage]="listingIdErrorMessage" />
        </form>
      </tidy-row>

      <tidy-row [alignLastItemRight]="true">
        <tidy-button
          [text]="'Add Listing'"
          [action]="addListing"
          class="primary" />
      </tidy-row>
    }

    @if (state === 'submitting') {
      <div class="loading-container">
        <tidy-loader />
      </div>
    }

    <!-- Connecting: header card with the inline listing-ID editor, then the platform's
         handover as an icon stepper and a "what happens next" note. -->
    @if (state === 'pending') {
      <tidy-card>
        <div class="listing-head">
          @if (platformMark) {
            <span class="platform-avatar" [ngClass]="platformAvatarClass">
              <img [src]="platformMark" [alt]="formatPlatform(listing.platform)" />
            </span>
          }
          <div class="listing-head-info">
            <tidy-text [header]="addressName || formatPlatform(listing.platform)" [translate]="false" />
            @if (fullAddress) {
              <div>
                <tidy-text [helper]="fullAddress" [translate]="false" />
              </div>
            }
            <div class="listing-head-status">
              <span class="status-glow-dot" [style.--dot-color]="listingStatusDotColor"></span>
              <tidy-text [body]="connectingLabel" />
            </div>
          </div>
        </div>
        <div class="cd-inline-id">
          <tidy-input
            [label]="listingIdLabel"
            [formControl]="pendingIdControl"
            [form]="pendingIdControl"
            [icon]="'assets/svg/home-outline.svg'"
            [submitted]="pendingIdSubmitted"
            [errorMessage]="pendingIdError" />
          <tidy-button
            [text]="isSavingPendingId ? 'Saving…' : 'Save'"
            [action]="savePendingListingId"
            [disabled]="!pendingIdDirty || isSavingPendingId"
            class="primary" />
        </div>
      </tidy-card>

      <div class="cd-steps-head">
        <tidy-text [header]="platformConnectTitle" />
        <tidy-text [helper]="platformConnectIntro" />
      </div>

      @if (airbnbCohostUrl || showCopyEmailLink) {
        <div class="cd-steps-actions">
          @if (airbnbCohostUrl) {
            <tidy-small-button
              [text]="'Open Co-Hosts Page on Airbnb'"
              [image]="'assets/svg/open-outline.svg'"
              (buttonClick)="openCohostsPage()" />
          }
          @if (showCopyEmailLink) {
            <tidy-small-button
              [text]="'Copy pm@tidy.com'"
              [image]="'assets/svg/copy-outline.svg'"
              (buttonClick)="copyEmail()" />
          }
        </div>
      }

      <div class="cd-stepper">
        @for (step of connectSteps; track step.title; let i = $index; let last = $last) {
          <div class="cd-step">
            <div class="cd-step-rail">
              <span class="cd-step-icon">
                <tidy-image [src]="step.icon" />
              </span>
              @if (!last) {
                <span class="cd-step-line"></span>
              }
            </div>
            <div class="cd-step-body">
              <span class="cd-step-eyebrow">{{ ('Step ' | translate) + (i + 1) }}</span>
              <tidy-text [body]="step.title" class="cd-step-title" />
              @if (step.detail) {
                <tidy-text [helper]="step.detail" />
              }
            </div>
          </div>
        }
      </div>

      <!-- Who the client sends their part to (Vrbo booking list / consent, Booking.com
           message) and how — the account manager runs these handovers. -->
      @if (contactPrompt) {
        <tidy-alert class="cd-contact">
          <div class="cd-contact-row">
            <img [src]="managerPhoto" [alt]="managerName" class="cd-contact-avatar" />
            <div class="cd-contact-text">
              <span class="cd-step-eyebrow">{{ 'Contact your account manager' | translate }}</span>
              <tidy-text [body]="managerName" [translate]="false" class="cd-step-title" />
              <tidy-text [helper]="contactPrompt" />
            </div>
          </div>
          <div class="cd-contact-actions">
            <tidy-small-button [text]="'Call'" [image]="'assets/svg/call-outline.svg'" (buttonClick)="callManager()" />
            <tidy-small-button [text]="'Email'" [image]="'assets/svg/mail-outline.svg'" (buttonClick)="emailManager()" />
            <tidy-small-button [text]="'Text'" [image]="'assets/svg/chatbox-ellipses-outline.svg'" (buttonClick)="textManager()" />
          </div>
        </tidy-alert>
      }

      <tidy-alert class="cd-next">
        <div class="cd-next-row">
          <span class="cd-next-icon">
            <tidy-image [src]="'assets/svg/hourglass-outline.svg'" />
          </span>
          <div class="cd-next-text">
            <tidy-text [body]="'What happens next'" class="cd-step-title" />
            @if (platformConnectOutro) {
              <tidy-text [helper]="platformConnectOutro" />
            }
            <div class="cd-next-waiting">
              <ion-spinner name="lines-small" />
              <tidy-text [helper]="'This page updates itself once the connection completes.'" />
            </div>
          </div>
        </div>
      </tidy-alert>
    }

    @if (state === 'syndicating') {
      <tidy-row>
        <tidy-text
          [body]="'Listing added.'" />
      </tidy-row>
      <tidy-alert>
        <tidy-row>
          <tidy-text
            [body]="('Your listing has been submitted to ' | translate) + formatPlatform(selectedPlatform) + ('. Syndication is pending and may take some time to appear.' | translate)" />
        </tidy-row>
      </tidy-alert>
    }

    @if (state === 'detail') {
      <tidy-card>
        <div class="listing-head">
          @if (platformMark) {
            <span class="platform-avatar" [ngClass]="platformAvatarClass">
              <img [src]="platformMark" [alt]="formatPlatform(listing.platform)" />
            </span>
          }
          <div class="listing-head-info">
            <tidy-text [header]="addressName || 'Listing Info'" />
            @if (fullAddress) {
              <div>
                <tidy-text [helper]="fullAddress" [translate]="false" />
              </div>
            }
            <div class="listing-head-status">
              <span class="status-glow-dot" [style.--dot-color]="listingStatusDotColor"></span>
              <tidy-text [body]="('Status: ' | translate) + formatStatus(listing.status)" />
              <span class="material-icons listing-info-hint"
                    [matTooltip]="infoTooltip | translate"
                    matTooltipClass="pre-line-tooltip"
                    [attr.aria-label]="infoTooltip">info</span>
            </div>
          </div>
        </div>
        @if (listing.isMockRoomListing) {
          <tidy-row>
            <tidy-text [body]="'Property: ' + listing.property_label" [translate]="false" />
          </tidy-row>
        }
        <tidy-card-button (action)="goToEditListing()">
          <tidy-row class="vertical-align-center">
            <tidy-image [src]="'assets/svg/create-outline.svg'" class="card-button-icon" />
            <tidy-text [body]="'Edit Listing'" />
          </tidy-row>
        </tidy-card-button>
        @if (viewListingUrl) {
          <tidy-card-button (action)="openViewListing()">
            <tidy-row class="vertical-align-center">
              <tidy-image [src]="'assets/svg/eye-outline.svg'" class="card-button-icon" />
              <tidy-text [body]="'View Listing'" />
            </tidy-row>
          </tidy-card-button>
        }
      </tidy-card>

      @if (listing.platform === 'airbnb') {
      <tidy-row class="extra-top-padding extra-bottom-padding">
        <tidy-text [header]="'Optimization History'" />
      </tidy-row>

      @if (runsLoading) {
        <div aria-hidden="true">
          @for (placeholder of skeletonRuns; track placeholder) {
            <tidy-card class="bs-report-card">
              <div class="bs-report-row bs-report-row--in-card">
                <span class="bs-report-marker optimizer-skeleton"></span>
                <div class="bs-report-main">
                  <span class="optimizer-skeleton optimizer-skeleton-line skeleton-run-name"></span>
                  <span class="optimizer-skeleton optimizer-skeleton-line skeleton-run-time"></span>
                </div>
              </div>
            </tidy-card>
          }
        </div>
      } @else if (runsError) {
        <tidy-alert>
          <tidy-row>
            <tidy-text [body]="'Could not load optimization history.'" />
          </tidy-row>
        </tidy-alert>
      } @else if (runs.length === 0) {
        <tidy-alert>
          <tidy-row>
            <tidy-text [body]="'No optimizations yet.'" />
          </tidy-row>
        </tidy-alert>
      } @else {
        @for (run of runs; track run.id) {
          <tidy-card class="bs-report-card">
            <div class="bs-report-row bs-report-row--clickable bs-report-row--in-card" (click)="openRun(run)">
              <span class="bs-report-marker" [ngClass]="getRunStatusClass(run.status)"></span>
              <div class="bs-report-main">
                <div class="bs-report-title">
                  <span class="bs-report-name">{{ addressName || formatPlatform(listing.platform) }}</span>
                  @if (run.status === 'success') {
                    <span class="bs-report-status status-success">
                      @if (runSuggestionCounts[run.id] !== null && runSuggestionCounts[run.id] !== undefined) {
                        {{ runSuggestionCounts[run.id] }} {{ runSuggestionCounts[run.id] === 1 ? 'change' : 'changes' }}
                      } @else {
                        {{ 'Completed' | translate }}
                      }
                    </span>
                  } @else {
                    <span class="bs-report-status" [ngClass]="getRunStatusClass(run.status)">{{ formatRunStatus(run.status) }}</span>
                  }
                </div>
                @if (addressCityState) {
                  <span class="bs-report-sub">{{ addressCityState }}</span>
                }
                <span class="bs-report-time">{{ formatRunDate(run.started_at || run.created_at) }}</span>
              </div>
              <ion-icon name="chevron-forward" class="bs-report-chevron" />
            </div>
          </tidy-card>
        }
      }
      }
    }

    @if (state === 'report') {
      @if (selectedRunReport) {
        <listing-optimizer-report
          [report]="selectedRunReport"
          [showHeader]="true"
          [historyMode]="true"
          [showBulkActions]="false"
          [listing]="listing" />
      } @else {
        <tidy-alert>
          <tidy-row>
            <tidy-text [body]="selectedRunUnavailable || 'No changes were applied in this optimization.'" />
          </tidy-row>
        </tidy-alert>
      }
    }
  </tidy-wrapper>
</ng-template>

@if (!windowService?.isDesktopRes) {
  <app-scroll-wrapper [templateContent]="content" />
} @else {
  <ng-container *ngTemplateOutlet="content" />
}
